import React, { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { fetchAllReturns, createReturn, deleteReturn } from "../api/ReturnsAPIs";
import { useAuth } from "../utils/auth";
import { useNavigate } from "react-router-dom";
import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";

const SkeletonLoader = ({ type }) => {
  if (type === "table") {
    return (
      <div className="animate-pulse">
        <div className="h-8 bg-gray-200 rounded mb-4"></div>
        {[...Array(5)].map((_, index) => (
          <div key={index} className="flex gap-4 border-b py-3">
            <div className="w-12 h-12 bg-gray-200 rounded"></div>
            <div className="flex-1 space-y-2">
              <div className="h-4 bg-gray-200 rounded w-3/4"></div>
              <div className="h-4 bg-gray-200 rounded w-1/2"></div>
            </div>
          </div>
        ))}
      </div>
    );
  }
  return null;
};

const ReturnsPage = () => {
  const navigate = useNavigate();
  const { authState } = useAuth();
  const queryClient = useQueryClient();
  const [showAddModal, setShowAddModal] = useState(false);
  const [formData, setFormData] = useState({
    orderRef: "",
    item: "",
    type: "",
    designLink: "",
  });
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Fetch all returns
  const { data: returns = [], isLoading, error } = useQuery({
    queryKey: ["allReturns"],
    queryFn: fetchAllReturns,
    staleTime: 5 * 60 * 1000, // Cache for 5 minutes
  });

  // Mutation for adding a return
  const addMutation = useMutation({
    mutationFn: createReturn,
    onSuccess: () => {
      toast.success("تم إضافة الإرجاع بنجاح!");
      setShowAddModal(false);
      setFormData({ orderRef: "", item: "", type: "", designLink: "" });
      queryClient.invalidateQueries(["allReturns"]); // Refresh list
    },
    onError: (err) => {
      toast.error(`خطأ في إضافة الإرجاع: ${err.message}`);
    },
  });

  // Mutation for deleting a return
  const deleteMutation = useMutation({
    mutationFn: deleteReturn,
    onSuccess: () => {
      toast.success("تم حذف الإرجاع بنجاح!");
      queryClient.invalidateQueries(["allReturns"]); // Refresh list
    },
    onError: (err) => {
      toast.error(`خطأ في حذف الإرجاع: ${err.message}`);
    },
  });

  // Role check
  React.useEffect(() => {
    if (authState.role !== "Admin" && authState.role !== "Moderator") {
      navigate("/login");
    }
  }, [authState.role, navigate]);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!formData.orderRef || !formData.item || !formData.type) {
      toast.error("يرجى ملء الحقول المطلوبة: OrderRef, Item, Type.");
      return;
    }
    setIsSubmitting(true);
    addMutation.mutate(formData);
    setIsSubmitting(false);
  };

  const handleDelete = (returnId) => {
    if (window.confirm(`هل أنت متأكد من حذف الإرجاع رقم ${returnId}؟`)) {
      deleteMutation.mutate(returnId);
    }
  };

  if (error) {
    return <div className="text-center text-red-500">خطأ في تحميل الإرجاعات: {error.message}</div>;
  }

  return (
    <div className="min-h-screen bg-gray-50 p-6" dir="rtl">
      <ToastContainer position="top-right" autoClose={3000} />
      <div>
        <div className="flex justify-between items-center mb-6">
          <h2 className="text-2xl font-bold">إدارة الإرجاعات</h2>
          <button
            onClick={() => navigate("/dashboard")}
            className="bg-gray-500 text-white px-4 py-2 rounded hover:bg-gray-600"
          >
            رجوع
          </button>
        </div>

        {/* Add Modal */}
        {showAddModal && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
            <div className="bg-white p-6 rounded-lg w-96">
              <h3 className="text-xl font-semibold mb-4">إضافة إرجاع جديد</h3>
              <form onSubmit={handleSubmit}>
                <div className="mb-4">
                  <label className="block text-sm font-medium mb-2">Order Ref (مطلوب)</label>
                  <input
                    type="text"
                    name="orderRef"
                    value={formData.orderRef}
                    onChange={handleInputChange}
                    className="w-full p-2 border rounded"
                    required
                  />
                </div>
                <div className="mb-4">
                  <label className="block text-sm font-medium mb-2">Item (مطلوب)</label>
                  <input
                    type="text"
                    name="item"
                    value={formData.item}
                    onChange={handleInputChange}
                    className="w-full p-2 border rounded"
                    required
                  />
                </div>
                <div className="mb-4">
                  <label className="block text-sm font-medium mb-2">Type (مطلوب)</label>
                  <input
                    type="text"
                    name="type"
                    value={formData.type}
                    onChange={handleInputChange}
                    className="w-full p-2 border rounded"
                    required
                  />
                </div>
                <div className="mb-6">
                  <label className="block text-sm font-medium mb-2">Design Link (اختياري)</label>
                  <input
                    type="url"
                    name="designLink"
                    value={formData.designLink}
                    onChange={handleInputChange}
                    className="w-full p-2 border rounded"
                  />
                </div>
                <div className="flex justify-end gap-2">
                  <button
                    type="button"
                    onClick={() => setShowAddModal(false)}
                    className="px-4 py-2 bg-gray-300 rounded hover:bg-gray-400"
                    disabled={isSubmitting}
                  >
                    إلغاء
                  </button>
                  <button
                    type="submit"
                    className="px-4 py-2 bg-blue-500 text-white rounded hover:bg-blue-600"
                    disabled={isSubmitting}
                  >
                    {isSubmitting ? "جاري الإضافة..." : "إضافة"}
                  </button>
                </div>
              </form>
            </div>
          </div>
        )}

        <div className="bg-white shadow-md rounded-lg p-6">
          <div className="flex justify-between mb-4">
            <h3 className="text-lg font-semibold">قائمة الإرجاعات</h3>
            <button
              onClick={() => setShowAddModal(true)}
              className="bg-green-500 text-white px-4 py-2 rounded hover:bg-green-600"
            >
              إضافة إرجاع جديد
            </button>
          </div>

          {isLoading ? (
            <SkeletonLoader type="table" />
          ) : returns.length > 0 ? (
            <div className="overflow-x-auto">
              <table className="w-full table-auto border-collapse border border-gray-300">
                <thead>
                  <tr className="bg-gray-100">
                    <th className="border w-12 text-right">Return ID</th>
                    <th className="border p-2 text-right">Order Ref</th>
                    <th className="border p-2 text-right">Item</th>
                    <th className="border p-2 text-right">Type</th>
                    <th className="border w-12 p-2 text-right">Design Link</th>
                    <th className="border p-2 text-right">إجراءات</th>
                  </tr>
                </thead>
                <tbody>
                  {returns.map((ret) => (
                    <tr key={ret.returnId} className="hover:bg-gray-50">
                      <td className="border p-2 text-right">{ret.returnId}</td>
                      <td className="border p-2 text-right">{ret.orderRef}</td>
                      <td className="border p-2 text-right">{ret.item}</td>
                      <td className="border p-2 text-right">{ret.type}</td>
                      <td className="border p-2 text-right">
                        {ret.designLink ? (
                          <a href={ret.designLink} target="_blank" rel="noopener noreferrer" className="text-blue-500 hover:underline">
                            رابط
                          </a>
                        ) : "لا يوجد"}
                      </td>
                      <td className="border p-2 text-right">
                        <button
                          onClick={() => handleDelete(ret.returnId)}
                          className="bg-red-500 text-white px-2 py-1 rounded hover:bg-red-600 mr-1"
                          disabled={deleteMutation.isPending}
                        >
                          حذف
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : (
            <p className="text-center text-gray-500">لا توجد إرجاعات</p>
          )}
        </div>
      </div>
    </div>
  );
};

export default ReturnsPage;